import React, { FC, useCallback, useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { CSSIsolation } from "@UI/Contexts/CSSIsolation";
import { DurationInput, formatDuration } from "@UI/Components/DurationInput/DurationInput";
import messages from "./messages";

export interface IHoursWorkedInputProps {
  /** Hours the field starts with (the legacy form starts at 1). */
  initialHours?: number;
  /** Smallest number of hours the SP can close a job with. */
  minHours?: number;
  /** The legacy form field that carries the decimal hours to the server. */
  inputSelector?: string;
}

const STEP_IN_MINUTES = 15;

type LegacyElementSet = {
  on: (event: string, handler: () => void) => void;
  off: (event: string, handler: () => void) => void;
};

/**
 * jQuery's `.val(x).trigger("change")` (newcontract.js) fires jQuery handlers
 * only, never native listeners — so hearing the legacy reset means going
 * through the page's own jQuery.
 */
const legacyJQuery = (): ((target: Element) => LegacyElementSet) | undefined =>
  (window as unknown as { jQuery?: (target: Element) => LegacyElementSet }).jQuery;

const hoursToMinutes = (hours: number) => Math.round(hours * 60);

/**
 * Hours-and-minutes input for the SP closing form (contracts/_apply). It owns
 * the UI and mirrors its value, as decimal hours, into the legacy
 * `#hour_worked` field — the one the form posts and `newcontract.js` reads to
 * recompute the amounts. An invalid value empties that field so the form's
 * `required` rule blocks the submit.
 */
export const HoursWorkedField: FC<IHoursWorkedInputProps> = ({
  initialHours = 1,
  minHours = 0.5,
  inputSelector = "#hour_worked",
}) => {
  const intl = useIntl();
  const minMinutes = hoursToMinutes(minHours);
  const [minutes, setMinutes] = useState<number | null>(hoursToMinutes(initialHours));

  const writeToLegacyField = useCallback(
    (value: number | null) => {
      const field = document.querySelector<HTMLInputElement>(inputSelector);
      if (!field) return;
      const next = value === null || value < minMinutes ? "" : String(value / 60);
      if (field.value === next) return;
      field.value = next;
      // A native bubbling event reaches jQuery's delegated `change` handlers.
      field.dispatchEvent(new Event("change", { bubbles: true }));
    },
    [inputSelector, minMinutes]
  );

  const handleChange = (value: number | null) => {
    setMinutes(value);
    writeToLegacyField(value);
  };

  // The legacy page resets the field itself (flat rate → 1); follow it.
  useEffect(() => {
    const field = document.querySelector<HTMLInputElement>(inputSelector);
    const jQuery = legacyJQuery();
    if (!field || !jQuery) return undefined;
    const syncFromLegacy = () => {
      // "" is our own invalid marker, not a reset.
      if (field.value === "") return;
      const fromLegacy = hoursToMinutes(Number(field.value));
      if (!Number.isNaN(fromLegacy)) setMinutes(fromLegacy);
    };
    jQuery(field).on("change", syncFromLegacy);
    return () => jQuery(field).off("change", syncFromLegacy);
  }, [inputSelector]);

  const isBelowMinimum = minutes !== null && minutes < minMinutes;
  const isInvalid = minutes === null || isBelowMinimum;

  return (
    <DurationInput
      valueInMinutes={minutes}
      onChange={handleChange}
      min={minMinutes}
      step={STEP_IN_MINUTES}
      isInvalid={isInvalid}
      errorMessage={
        isBelowMinimum
          ? intl.formatMessage(messages.belowMinimum, { min: formatDuration(minMinutes) })
          : intl.formatMessage(messages.invalid)
      }
    />
  );
};

/**
 * The closing page is legacy: its global `button`/`input` rules would square
 * off and resize the Chakra controls, so the field renders in a shadow root.
 */
export const HoursWorkedInput: FC<IHoursWorkedInputProps> = (props) => (
  <CSSIsolation>
    <HoursWorkedField {...props} />
  </CSSIsolation>
);
